import { forwardRef } from 'react'
import { IconButton, Box, Stack, type Theme, type SxProps, useTheme, Typography } from '@dhl/theme'
import { ChevronLeft, Close } from '@dhl/icons'
import { swipeDialogClasses } from '../classes'

const styles: Record<string, SxProps<Theme>> = {
  closeHandleContainer: {
    display: 'flex',
    height: 32,
    justifyContent: 'center',
  },
  closeHandle: {
    width: '50px',
    height: '6px',
    borderRadius: '50px',
    backgroundColor: 'grey.300',
  },
  backButtonContainer: ({ spacing }) => ({
    marginLeft: spacing(-0.25),
    marginRight: spacing(1),
  }),
  backButton: ({ spacing }) => ({
    padding: spacing(0, 1),
  }),
}

type DialogHeaderProps = {
  title?: React.ReactNode
  onClose?: () => void
  onBack?: () => void
}

export const DialogHeader = forwardRef(
  ({ title, onClose, onBack }: DialogHeaderProps, ref: React.ForwardedRef<HTMLDivElement | null>) => {
    const {
      device: { isMobile },
    } = useTheme()

    if (!title && !onClose) {
      return null
    }

    const showHandle = isMobile && !!onClose

    return (
      <Stack
        className={swipeDialogClasses.dialogHeader}
        ref={ref}
        sx={({ spacing }) => ({
          position: 'relative',
          zIndex: 2000,
          padding: spacing(isMobile ? 1 : 2, 2, 0, 2),
        })}
      >
        {isMobile && <Box sx={styles.closeHandleContainer}>{showHandle && <Box sx={styles.closeHandle} />}</Box>}
        <Stack
          sx={({ spacing }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            height: 'fit-content',
            marginBottom: spacing(isMobile ? 2 : 0),
            marginTop: spacing(-0.5),
            width: isMobile ? 'calc(100% - 40px)' : '100%', // take the close button width into account to prevent overlapping
          })}
        >
          {onBack && (
            <Box data-testid="swipe-dialog-header-back-button" sx={styles.backButtonContainer}>
              <IconButton disableRipple sx={styles.backButton} onClick={onBack}>
                <ChevronLeft sx={{ width: 24, height: 24 }} />
              </IconButton>
            </Box>
          )}
          {title && <Typography variant="h4">{title}</Typography>}

          {onClose && (
            <IconButton
              size="small"
              data-testid="dialog-title-closable"
              sx={({ spacing }) => ({
                ...(isMobile
                  ? {
                      position: 'absolute',
                      top: spacing(2),
                      right: spacing(2),
                      backgroundColor: 'grey.300',
                    }
                  : {
                      marginLeft: 'auto',
                    }),
              })}
              onClick={onClose}
            >
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>
    )
  }
)
